import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { OwlMomentDateTimeModule } from '@danielmoncada/angular-datetime-picker-moment-adapter';
import {
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
  PERFECT_SCROLLBAR_CONFIG
} from '../shared/directives/perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { AppErrorHandler } from './error/app-error.handler';
import { TranslateLocalLoader } from './factories/translate-local-loader';
import { LoggedInGuard } from './guards/logged-in.guard';
import { NotLoggedInGuard } from './guards/not-logged-in.guard';
import { AuthApiService } from './services/api/auth-api.service';
import { CompanyApiService } from './services/api/company-api.service';
import { ErrorApiService } from './services/api/error-api.service';
import { LanguageApiService } from './services/api/language-api.service';
import { LevelApiService } from './services/api/level-api.service';
import { PollApiService } from './services/api/poll-api.service';
import { PollGroupApiService } from './services/api/poll-group-api.service';
import { SettingApiService } from './services/api/setting-api.service';
import { SectorApiService } from './services/api/sector-api.service';
import { TargetApiService } from './services/api/target-api.service';
import { RefreshTokenService } from './services/authentication/refresh-token.service';
import { EmptyResponseInterceptor } from './services/base/interceptors/empty-response.interceptor';
import { ErrorInterceptor } from './services/base/interceptors/error.interceptor';
import { JwtInterceptor } from './services/base/interceptors/jwt.interceptor';
import { PendingInterceptor } from './services/base/interceptors/pending.interceptor';
import { RefreshTokenInterceptor } from './services/base/interceptors/refresh-token.interceptor';
import { PendingService } from './services/base/pending.service';
import { ResolverService } from './services/base/resolver.service';
import { TranslationService } from './services/translation.service';
import { ValidationService } from './services/validation/validation.service';
import { TitlesService } from './services/visual/titles.service';
import { ToasterService } from './services/visual/toaster.service';
import { TokenGuard } from './guards/token.guard';
import { ResearchUnitApiService } from './services/api/research-unit-api.service';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelSpeed: 1,
  wheelPropagation: false,
  minScrollbarLength: null
};

@NgModule({
  imports: [
    HttpClientModule,
    OwlDateTimeModule,
    OwlMomentDateTimeModule,
    PerfectScrollbarModule,
    ToastrModule.forRoot({
      autoDismiss: true,
      maxOpened: 1,
      positionClass: 'toast-top-center',
      preventDuplicates: false,
      timeOut: environment.toasterTimeout
    }),
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: TranslateLocalLoader }
    })
  ],
  exports: [
    TranslateModule
  ],
  providers: [
    // Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: PendingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EmptyResponseInterceptor, multi: true },
    // Handlers
    { provide: ErrorHandler, useClass: AppErrorHandler },
    // Perfect scroll config
    { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
    // API Services
    AuthApiService,
    CompanyApiService,
    ErrorApiService,
    LanguageApiService,
    LevelApiService,
    PollApiService,
    PollGroupApiService,
    // Services
    PendingService,
    RefreshTokenService,
    ResolverService,
    SectorApiService,
    SettingApiService,
    ResearchUnitApiService,
    TargetApiService,
    TitlesService,
    ToasterService,
    TranslationService,
    ValidationService,
    // Guards
    LoggedInGuard,
    NotLoggedInGuard,
    TokenGuard,
  ]
})
export class CoreModule { }
